import { Capacitor } from '@capacitor/core';

// The Web Speech API's recognizer and its events, which lib.dom does not type.
export interface BrowserSpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

export interface BrowserSpeechRecognitionResult {
  isFinal: boolean;
  length: number;
  [index: number]: BrowserSpeechRecognitionAlternative;
}

export interface BrowserSpeechRecognitionResultList {
  length: number;
  [index: number]: BrowserSpeechRecognitionResult;
}

export interface BrowserSpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: BrowserSpeechRecognitionResultList;
}

export interface BrowserSpeechRecognitionErrorEvent extends Event {
  error: string;
  message?: string;
}

export interface BrowserSpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  onresult: ((event: BrowserSpeechRecognitionEvent) => void) | null;
  onerror: ((event: BrowserSpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}

export type BrowserSpeechRecognitionConstructor =
  new () => BrowserSpeechRecognition;

export const getSpeechRecognitionConstructor =
  (): BrowserSpeechRecognitionConstructor | null => {
    if (typeof window === 'undefined' || Capacitor.isNativePlatform()) {
      return null;
    }

    const speechWindow = window as Window &
      typeof globalThis & {
        SpeechRecognition?: BrowserSpeechRecognitionConstructor;
        webkitSpeechRecognition?: BrowserSpeechRecognitionConstructor;
      };

    return (
      speechWindow.SpeechRecognition ||
      speechWindow.webkitSpeechRecognition ||
      null
    );
  };

export const normalizeTranscriptText = (text: string) =>
  text.replace(/\s+/g, ' ').trim();

export const buildSpeechDraft = (baseText: string, transcript: string) => {
  const normalizedTranscript = normalizeTranscriptText(transcript);
  if (!normalizedTranscript) return baseText;
  if (!baseText.trim()) return normalizedTranscript;
  return `${baseText}${/\s$/.test(baseText) ? '' : ' '}${normalizedTranscript}`;
};
